import React, { Component } from "react";
import { Link, NavLink } from "react-router-dom";
import PerfectScrollbar from "react-perfect-scrollbar";
import { doc, getDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebaseConfig";
import {
  dashboardMenu,
  applicationsMenu,
  pagesMenu,
  uiElementsMenu,
} from "../data/Menu";

export default class Sidebar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      username: "",
      email: "",
      profilePicture: null,
      role: "",
      isAdmin: false,
      permissions: {},
    };
  }

  fetchUserData = async (user) => {
    try {
      const userDocRef = doc(db, "Users", user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userRole = userData.role || "default";

        this.setState({
          username: userData.fullName || "Unknown User",
          email: userData.email || "No email",
          profilePicture: userData.profilePicture || null,
          role: userRole,
          isAdmin: userRole === "admin",
        });

        if (userRole === "admin") {
          this.setState({
            permissions: {
              canAccessPayrolls: true,
              canApproveDenyLeaves: true,
              canClockInOut: true,
              canManageLeaves: true,
              canManagePayrolls: true,
              canUploadDocuments: true,
              canViewClockInOut: true,
              canViewInvoice: true,
              canViewYearlyDocuments: true,
            },
          });
        } else {
          const roleDocRef = doc(db, "Roles", userRole);
          const roleDoc = await getDoc(roleDocRef);

          if (roleDoc.exists()) {
            this.setState({
              permissions: roleDoc.data().permissions || {},
            });
          } else {
            console.warn(`No role document found for role: ${userRole}`);
          }
        }
      } else {
        console.warn("User document does not exist in Firestore");
      }
    } catch (error) {
      console.error("Error fetching user data or permissions:", error);
    }
  };

  componentDidMount() {
    const savedSkin = localStorage.getItem("sidebar-skin") || "dark";
    this.setSidebarSkin(savedSkin);

    const auth = getAuth();

    this.authUnsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        this.fetchUserData(user);
      } else {
        console.warn("No user is signed in.");
        this.setState({
          username: "",
          email: "",
          profilePicture: null,
          role: "",
          isAdmin: false,
          permissions: {},
        });
      }
    });
  }

  componentWillUnmount() {
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
    }
  }

  setSidebarSkin = (skin) => {
    const HTMLTag = document.querySelector("html");
    HTMLTag.setAttribute("data-sidebar", skin);
    localStorage.setItem("sidebar-skin", skin);
  };

  toggleFooterMenu = (e) => {
    e.preventDefault();
    const parent = e.target.closest(".sidebar");
    parent.classList.toggle("footer-menu-show");
  };

  render() {
    const { username, email, profilePicture, isAdmin, permissions } = this.state;

    return (
      <div className="sidebar">
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            ZEVELON
          </Link>
        </div>
        <PerfectScrollbar
          className="sidebar-body"
          ref={(ref) => (this._scrollBarRef = ref)}
        >
          <SidebarMenu
            isAdmin={isAdmin}
            permissions={permissions}
            onUpdateSize={() => this._scrollBarRef?.updateScroll()}
          />
        </PerfectScrollbar>
        <div className="sidebar-footer">
          <div className="sidebar-footer-top">
            <div className="sidebar-footer-thumb">
              {profilePicture ? (
                <img
                  src={`data:image/jpeg;base64,${profilePicture}`}
                  alt="Profile"
                  className="rounded-circle"
                  style={{ width: "50px", height: "50px", objectFit: "cover" }}
                />
              ) : (
                <div className="avatar-circle">{username.charAt(0)}</div>
              )}
            </div>
            <div className="sidebar-footer-body">
              <h6>
                <Link to="/">{username}</Link>
              </h6>
              <p>{email}</p>
            </div>
            <Link
              onClick={this.toggleFooterMenu}
              to=""
              className="dropdown-link"
            >
              <i className="ri-arrow-down-s-line"></i>
            </Link>
          </div>
          <div className="sidebar-footer-menu">
            <nav className="nav">
              <Link to="/pages/profile">
                <i className="ri-edit-2-line"></i> Edit Profile
              </Link>
              <Link to="/pages/profile">
                <i className="ri-profile-line"></i> View Profile
              </Link>
            </nav>
            <hr />
            <nav className="nav">
              <Link to="">
                <i className="ri-question-line"></i> Help Center
              </Link>
              <Link to="">
                <i className="ri-lock-line"></i> Privacy Settings
              </Link>
              <Link to="">
                <i className="ri-user-settings-line"></i> Account Settings
              </Link>
              <Link to="/pages/signout">
                <i className="ri-logout-box-r-line"></i> Log Out
              </Link>
            </nav>
          </div>
        </div>
      </div>
    );
  }
}

class SidebarMenu extends Component {
  filterMenuByPermissions = (menu) => {
    const { permissions, isAdmin } = this.props;

    return menu.filter((item) => {
      if (isAdmin) {
        return true;
      }

      const permissionMap = {
        Payroll: "canAccessPayrolls",
        "Approve Leaves": "canApproveDenyLeaves",
        "Clock In & Out": "canClockInOut",
        "Manage Leaves": "canManageLeaves",
        "Manage Payrolls": "canManagePayrolls",
        "File Manager": "canUploadDocuments",
        "User Clocks": "canViewClockInOut",
        Invoice: "canViewInvoice",
        "Yearly Document": "canViewYearlyDocuments",
        "Create Invoice": "canCreateInvoices",
        Maintaince: "admin",
        Roles: "admin",
        Arise: "canViewPortalArise",
        "Create Users": "admin",
        "Signed Documents": "canUploadDocuments",
      };

      const requiredPermission = permissionMap[item.label];
      return requiredPermission ? permissions[requiredPermission] || false : true;
    });
  };

  populateMenu = (menu) => {
    const filteredMenu = this.filterMenuByPermissions(menu);

    return (
      <ul className="nav nav-sidebar">
        {filteredMenu.map((item, key) => (
          <li key={key} className="nav-item">
            {!item.submenu ? (
              item.label === "Arise" ? (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nav-link"
                >
                  <i className={item.icon}></i> <span>{item.label}</span>
                </a>
              ) : (
                <NavLink to={item.link} className="nav-link">
                  <i className={item.icon}></i> <span>{item.label}</span>
                </NavLink>
              )
            ) : (
              <div onClick={this.toggleSubMenu} className="nav-link has-sub">
                <i className={item.icon}></i> <span>{item.label}</span>
              </div>
            )}
            {item.submenu && (
              <nav className="nav nav-sub">
                {item.submenu.map((subItem, subKey) => (
                  <NavLink
                    to={subItem.link}
                    className="nav-sub-link"
                    key={subKey}
                  >
                    {subItem.label}
                  </NavLink>
                ))}
              </nav>
            )}
          </li>
        ))}
      </ul>
    );
  };

  toggleSubMenu = (e) => {
    e.preventDefault();
    const parent = e.target.closest(".nav-item");
    parent.classList.toggle("show");
    this.props.onUpdateSize();
  };

  toggleMenu = (e) => {
    e.preventDefault();
    const parent = e.target.closest(".nav-group");
    parent.classList.toggle("show");
    this.props.onUpdateSize();
  };

  render() {
    return (
      <React.Fragment>
        <div className="nav-group show">
          <div className="nav-label" onClick={this.toggleMenu}>
            Dashboard
          </div>
          {this.populateMenu(dashboardMenu)}
        </div>
      </React.Fragment>
    );
  }
}
